import { Divider, Space, Text } from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { FundAdditionalAttributesModel } from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { NEST_ITEM_CONNECTOR } from '@/utils';
import { FundSheetTypeLabelModifier } from '@/utils/enum';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const SheetTypeMetaInformation = FundAdditionalAttributesModel.pick({
  fundSheetType: true,
  fundSheetNumber: true,
  fundSheetBranchNumber: true,
}).extend({ isBefore2025: z.boolean() });

export const KsSheetTypeMetaInformation = ({
  fundSheetType,
  fundSheetNumber,
  fundSheetBranchNumber,
}: z.infer<typeof SheetTypeMetaInformation>) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const pinpointSheetType =
    fundSheetType === 'investment'
      ? 'KS_INVESTMENT'
      : fundSheetType === 'standard'
        ? 'KS_STANDARD'
        : 'KS_LOCAL_GOVERNMENT';
  return (
    <Space split={<Divider type="vertical" />}>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          ksInvestment: {
            field: 'ks.investment.fund-sheet-type',
            path: { project: projectId },
          },
          ksStandard: {
            field: 'ks.standard.fund-sheet-type',
            path: { project: projectId },
          },
          ksLocalGovernment: {
            field: 'ks.local-government.fund-sheet-type',
            path: { project: projectId },
          },
        })}
      >
        <Space>
          <Text variant="footnote" text={t('sheet-type')} color="secondary" />
          <Text
            variant="footnote"
            text={[
              t('fund-sheet'),
              FundSheetTypeLabelModifier[fundSheetType],
            ].join(NEST_ITEM_CONNECTOR)}
          />
        </Space>
      </PinpointMarker>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          ksInvestment: {
            field: 'ks.investment.fund-sheet-number',
            path: { project: projectId },
          },
          ksStandard: {
            field: 'ks.standard.fund-sheet-number',
            path: { project: projectId },
          },
          ksLocalGovernment: {
            field: 'ks.local-government.fund-sheet-number',
            path: { project: projectId },
          },
        })}
      >
        <Space>
          <Text
            variant="footnote"
            color="secondary"
            text={t('fund-sheet-number')}
          />
          <Text variant="footnote" text={fundSheetNumber} />
        </Space>
      </PinpointMarker>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType,
          ksInvestment: {
            field: 'ks.investment.fund-sheet-branch-number',
            path: { project: projectId },
          },
          ksStandard: {
            field: 'ks.standard.fund-sheet-branch-number',
            path: { project: projectId },
          },
          ksLocalGovernment: {
            field: 'ks.local-government.fund-sheet-branch-number',
            path: { project: projectId },
          },
        })}
      >
        <Space>
          <Text
            variant="footnote"
            color="secondary"
            text={t('fund-sheet-branch-number')}
          />
          <Text variant="footnote" text={fundSheetBranchNumber} />
        </Space>
      </PinpointMarker>
    </Space>
  );
};
